import React, { useRef, useState } from 'react';
import { Files, Guests as GuestsApi } from '../lib/api';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { FileText, Upload, Download, Maximize2, Trash2, Loader2, Sparkles, HelpCircle } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const fmtSize = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export function TalkingPointsPanel({ guest, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const tp = guest.talkingPoints;
  const [ai, setAi] = useState({ open: false, step: 'prompt', busy: false, question: null, summary: '', clarification: '', done: null, prompt: guest.talkingPointsPrompt || '', defaultPrompt: '', showDefault: false });

  const openAi = async () => {
    setAi(a => ({ ...a, open: true, step: 'prompt', busy: false, question: null, done: null, prompt: guest.talkingPointsPrompt || '' }));
    if (!ai.defaultPrompt) {
      try { const r = await GuestsApi.talkingPointsDefaultPrompt(); setAi(a => ({ ...a, defaultPrompt: r.prompt })); } catch (e) { /* ignore */ }
    }
  };

  const runAi = async (confirm = false) => {
    setAi(a => ({ ...a, open: true, step: 'run', busy: true }));
    try {
      const r = await GuestsApi.aiTalkingPoints(guest.id, { confirm, clarification: ai.clarification, customPrompt: ai.prompt });
      if (r.needsConfirmation) {
        setAi(a => ({ ...a, busy: false, question: r.question, summary: r.summary, done: null }));
        return;
      }
      onChange(r.talkingPoints);
      if ((guest.talkingPointsPrompt || '') !== ai.prompt) onChange({ talkingPointsPrompt: ai.prompt }, 'prompt');
      setAi(a => ({ ...a, busy: false, question: null, done: r }));
    } catch (e) {
      setAi(a => ({ ...a, busy: false }));
      toast({ title: 'Could not draft talking points', description: e?.response?.data?.detail || e.message });
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      toast({ title: 'PDF only', description: 'Export your talking points as a PDF so they can be read on screen while recording.' });
      return;
    }
    setUploading(true);
    try {
      const r = await Files.upload(file);
      onChange({ url: r.url, name: r.name, size: r.size, uploadedAt: new Date().toISOString() });
      toast({ title: 'Talking points uploaded' });
    } catch (e) {
      toast({ title: 'Upload failed', description: e?.response?.data?.detail || e.message });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-3" data-testid="talking-points-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <Label className="text-[11px] uppercase text-stone-500">Talking points (PDF)</Label>
          <p className="text-[12px] text-stone-500 mt-0.5">Your prep notes for this recording. Open full screen to scroll through them while the show is live.</p>
        </div>
        <Button size="sm" variant="outline" className="h-8 shrink-0 border-violet-300 text-violet-700 hover:bg-violet-50" onClick={openAi} disabled={ai.busy} data-testid="ai-talking-points-btn">
          <Sparkles className="w-3.5 h-3.5 mr-1.5" /> {tp?.url ? 'Redraft with AI' : 'Draft with AI'}
        </Button>
      </div>

      {tp?.url ? (
        <div className="flex items-center gap-3 p-3 rounded-md border border-stone-200 bg-stone-50" data-testid="talking-points-file">
          <div className="w-10 h-12 rounded bg-white border border-stone-200 grid place-items-center shrink-0">
            <FileText className="w-5 h-5 text-red-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-medium text-stone-900 truncate">{tp.name}</div>
            <div className="text-[11.5px] text-stone-500">{fmtSize(tp.size || 0)}{tp.uploadedAt ? ` · uploaded ${new Date(tp.uploadedAt).toLocaleDateString()}` : ''}</div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button asChild size="sm" className="h-8 bg-stone-900 hover:bg-stone-800 text-white" data-testid="talking-points-open-btn">
              <a href={`/prep/${guest.id}`} target="_blank" rel="noreferrer"><Maximize2 className="w-3.5 h-3.5 mr-1.5" /> Open full screen</a>
            </Button>
            <Button asChild size="sm" variant="outline" className="h-8" data-testid="talking-points-download-btn">
              <a href={`${tp.url}?download=1`}><Download className="w-3.5 h-3.5" /></a>
            </Button>
            <Button size="sm" variant="ghost" className="h-8 text-red-600 hover:bg-red-50" onClick={() => onChange(null)} data-testid="talking-points-remove-btn" title="Remove">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      ) : null}

      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files?.[0]); }}
        onClick={() => inputRef.current?.click()}
        data-testid="talking-points-dropzone"
        className={`cursor-pointer rounded-md border-2 border-dashed p-4 text-center transition-colors ${drag ? 'border-stone-500 bg-stone-100' : 'border-stone-300 hover:border-stone-400 hover:bg-stone-50'}`}
      >
        <input ref={inputRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="talking-points-input" />
        <div className="flex items-center justify-center gap-2 text-[12.5px] text-stone-600">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? 'Uploading…' : tp?.url ? 'Drop a new PDF to replace' : 'Drop a PDF here or click to upload'}
        </div>
        <div className="text-[11px] text-stone-400 mt-1">Max 11 MB</div>
      </div>

      <Dialog open={ai.open} onOpenChange={(o) => !ai.busy && setAi(a => ({ ...a, open: o }))}>
        <DialogContent className="max-w-lg" data-testid="ai-talking-points-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[15px]"><Sparkles className="w-4 h-4 text-violet-600" /> AI talking points for {guest.name}</DialogTitle>
          </DialogHeader>
          {!ai.busy && ai.step === 'prompt' && (
            <div className="space-y-3" data-testid="ai-talking-points-prompt-step">
              <div className="text-[12.5px] text-stone-600">
                The AI researches {guest.name}, checks it has the right person, then writes the prep sheet. By default: a 75-word intro, 10 personal-history, 10 business-history and 10 philosophy questions.
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-[11px] uppercase text-stone-500">Custom instructions for this guest (optional)</Label>
                  <button type="button" className="text-[11px] text-violet-700 hover:underline" onClick={() => setAi(a => ({ ...a, showDefault: !a.showDefault }))} data-testid="ai-talking-points-show-default">
                    {ai.showDefault ? 'Hide default prompt' : 'View default prompt'}
                  </button>
                </div>
                {ai.showDefault && (
                  <pre className="whitespace-pre-wrap text-[11.5px] leading-relaxed text-stone-600 bg-stone-50 border border-stone-200 rounded p-2.5 mb-2 max-h-40 overflow-auto" data-testid="ai-talking-points-default-prompt">{ai.defaultPrompt || 'Loading…'}</pre>
                )}
                <Textarea
                  data-testid="ai-talking-points-prompt"
                  rows={6}
                  value={ai.prompt}
                  onChange={(e) => setAi(a => ({ ...a, prompt: e.target.value }))}
                  placeholder="Leave empty to use the default. Or override, e.g. “Skip the intro. 5 questions about their 2019 harvest fire, 10 about natural winemaking, 5 rapid-fire fun questions. Keep it playful.”"
                />
                <div className="flex items-center justify-between mt-1">
                  <div className="text-[11px] text-stone-400">{ai.prompt.trim() ? 'Your instructions replace the default and are saved with this guest.' : 'Using the default instructions.'}</div>
                  {ai.prompt.trim() && <button type="button" className="text-[11px] text-stone-500 hover:underline" onClick={() => setAi(a => ({ ...a, prompt: '' }))} data-testid="ai-talking-points-prompt-clear">Use default instead</button>}
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setAi(a => ({ ...a, open: false }))}>Cancel</Button>
                <Button data-testid="ai-talking-points-start" onClick={() => runAi(false)} className="bg-violet-600 hover:bg-violet-700 text-white">
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Research &amp; draft
                </Button>
              </DialogFooter>
            </div>
          )}
          {ai.busy && (
            <div className="py-8 text-center text-[13px] text-stone-600" data-testid="ai-talking-points-busy">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2 text-violet-600" />
              {ai.question ? 'Writing the intro and 30 questions…' : 'Researching the guest and checking we have the right person…'}
              <div className="text-[11.5px] text-stone-400 mt-1">Usually 20–40 seconds</div>
            </div>
          )}
          {!ai.busy && ai.question && (
            <div className="space-y-3" data-testid="ai-talking-points-question">
              <div className="flex gap-2 p-3 rounded-md bg-amber-50 border border-amber-200 text-[13px] text-amber-900">
                <HelpCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium">I want to be sure I have the right person.</div>
                  <div className="mt-1">{ai.question}</div>
                  {ai.summary && <div className="mt-1 text-[12px] text-amber-800/80">What I found: {ai.summary}</div>}
                </div>
              </div>
              <div>
                <Label className="text-[11px] uppercase text-stone-500">Tell me who they are (optional, but helps)</Label>
                <Textarea data-testid="ai-talking-points-clarification" rows={3} value={ai.clarification} onChange={(e) => setAi(a => ({ ...a, clarification: e.target.value }))} placeholder="e.g. Yes — she's the winemaker at Domaine X in Paso Robles, took over from her father in 2015." />
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setAi(a => ({ ...a, open: false }))}>Cancel</Button>
                <Button data-testid="ai-talking-points-confirm" onClick={() => runAi(true)} className="bg-violet-600 hover:bg-violet-700 text-white">
                  {ai.clarification.trim() ? 'Use my notes and draft' : "Yes, that's them — draft"}
                </Button>
              </DialogFooter>
            </div>
          )}
          {!ai.busy && ai.done && (
            <div className="space-y-3" data-testid="ai-talking-points-done">
              <div className="text-[13px] text-stone-700">Done — {ai.done.talkingPoints?.customPrompt ? 'drafted from your custom instructions' : 'intro plus 10 personal, 10 business and 10 philosophy questions'}{ai.done.sourceCount ? `, drawn from ${ai.done.sourceCount} sources` : ''}. Please fact-check before air.</div>
              {ai.done.intro && <div className="p-3 rounded-md bg-stone-50 border border-stone-200 text-[12.5px] leading-relaxed text-stone-700 italic">“{ai.done.intro}”</div>}
              {ai.done.researchErrors?.length > 0 && <div className="text-[11.5px] text-amber-700">Web research was limited: {ai.done.researchErrors[0]}</div>}
              <DialogFooter>
                <Button variant="ghost" onClick={() => setAi(a => ({ ...a, open: false, done: null }))}>Close</Button>
                <Button asChild className="bg-stone-900 hover:bg-stone-800 text-white"><a href={`/prep/${guest.id}`} target="_blank" rel="noreferrer"><Maximize2 className="w-3.5 h-3.5 mr-1.5" /> Open full screen</a></Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
